import {
  Cell,
  CellType,
  FletchyVault,
  FletchyVaultSchema,
  ImportResult,
  Note,
  Notebook,
  SCHEMA_VERSION,
  VaultData,
} from "./types";

const ID_RANDOM_BYTES = 8;
const utf8Decoder = new TextDecoder();
const utf8Encoder = new TextEncoder();

export function nowIso(): string {
  return new Date().toISOString();
}

export function createId(prefix: string): string {
  const bytes = new Uint8Array(ID_RANDOM_BYTES);
  globalThis.crypto?.getRandomValues(bytes);
  const random = Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
  return `${prefix}_${Date.now().toString(36)}_${random}`;
}

export function createNotebook(title = "Notebook", sortOrder = 0): Notebook {
  const now = nowIso();
  return {
    id: createId("nb"),
    title,
    sortOrder,
    createdAt: now,
    updatedAt: now,
  };
}

export function createNote(notebookId: string, title = "Untitled Note", sortOrder = 0): Note {
  const now = nowIso();
  return {
    id: createId("note"),
    notebookId,
    title,
    tags: [],
    favorite: false,
    createdAt: now,
    updatedAt: now,
    cellIds: [],
  };
}

export function createCell(
  noteId: string,
  type: CellType = "markdown",
  content = "",
  sortOrder = 0,
  metadata: Cell["metadata"] = {},
): Cell {
  return {
    id: createId("cell"),
    noteId,
    type,
    content,
    sortOrder,
    metadata,
  };
}

export function createEmptyVault(): FletchyVault {
  const now = nowIso();
  const notebook = createNotebook("Personal", 0);
  const note = createNote(notebook.id, "Welcome to Fletchy", 0);
  const cell = createCell(
    note.id,
    "markdown",
    [
      "# Welcome",
      "",
      "Fletchy stores your notes locally in this browser first. Create notebooks, mix cells, search fast, and export a `.fletchy` vault whenever you want.",
    ].join("\n"),
    0,
  );

  return normalizeVault({
    schemaVersion: SCHEMA_VERSION,
    notebooks: [notebook],
    notes: [{ ...note, cellIds: [cell.id] }],
    cells: [cell],
    settings: {
      theme: "system",
      splitPreview: true,
    },
    createdAt: now,
    updatedAt: now,
  });
}

export function createDemoVault(): FletchyVault {
  const now = nowIso();
  const notebook = createNotebook("Demo Notebook", 0);
  const note = createNote(notebook.id, "Quiver-style mixed cells", 0);
  const cells: Cell[] = [
    createCell(
      note.id,
      "markdown",
      [
        "# Mixed cells",
        "",
        "Use Markdown for prose, lists, links, and checklists.",
        "",
        "- Local-first IndexedDB storage",
        "- `.fletchy` export/import",
        "- Safe previews without raw HTML",
      ].join("\n"),
      0,
    ),
    createCell(note.id, "code", "function greet(name: string) {\n  return `Hello ${name}`;\n}", 1, {
      language: "typescript",
    }),
    createCell(note.id, "latex", "E = mc^2", 2),
    createCell(note.id, "mermaid", "flowchart LR\n  Quiver --> Import\n  Import --> Fletchy\n  Fletchy --> Export", 3),
  ];

  const secondNote = createNote(notebook.id, "Search, tags and favorites", 1);
  secondNote.tags = ["local-first", "workflow"];
  secondNote.favorite = true;
  const secondCell = createCell(
    secondNote.id,
    "text",
    "This demo vault is isolated seed data. Reset it whenever you want without touching another vault.",
    0,
  );

  return normalizeVault({
    schemaVersion: SCHEMA_VERSION,
    notebooks: [notebook],
    notes: [
      { ...note, tags: ["demo", "quiver"], cellIds: cells.map((cell) => cell.id) },
      { ...secondNote, cellIds: [secondCell.id] },
    ],
    cells: [...cells, secondCell],
    settings: {
      theme: "system",
      splitPreview: true,
    },
    createdAt: now,
    updatedAt: now,
  });
}

export function normalizeVault(vault: FletchyVault): FletchyVault {
  const parsed = FletchyVaultSchema.parse(vault);
  const notebooks = [...parsed.notebooks].sort((a, b) => a.sortOrder - b.sortOrder);
  const notes = [...parsed.notes].sort((a, b) => a.createdAt.localeCompare(b.createdAt));
  const cells = [...parsed.cells].sort((a, b) => a.sortOrder - b.sortOrder);
  const cellIds = new Set(cells.map((cell) => cell.id));

  return {
    ...parsed,
    notebooks,
    notes: notes.map((note) => ({
      ...note,
      tags: Array.from(new Set(note.tags.map((tag) => tag.trim()).filter(Boolean))).sort(),
      cellIds: note.cellIds.filter((cellId) => cellIds.has(cellId)),
    })),
    cells,
    updatedAt: nowIso(),
  };
}

function stringFromVaultData(data: VaultData): string {
  if (typeof data === "string") {
    return data;
  }

  return utf8Decoder.decode(data);
}

function migrateVault(value: unknown): FletchyVault {
  const version = (value as { schemaVersion?: unknown } | null)?.schemaVersion;

  if (version !== SCHEMA_VERSION) {
    throw new Error(`Unsupported Fletchy vault schema version "${String(version)}". Supported version: ${SCHEMA_VERSION}.`);
  }

  return FletchyVaultSchema.parse(value);
}

export function parseVault(data: VaultData): FletchyVault {
  const value = JSON.parse(stringFromVaultData(data)) as unknown;
  return normalizeVault(migrateVault(value));
}

export function serializeVault(vault: FletchyVault): string {
  return JSON.stringify(normalizeVault(vault), null, 2);
}

export function serializeVaultBytes(vault: FletchyVault): Uint8Array {
  return utf8Encoder.encode(serializeVault(vault));
}

export function createImportResult(
  importedCounts: ImportResult["importedCounts"] = {},
  skippedCounts: ImportResult["skippedCounts"] = {},
  warnings: string[] = [],
): ImportResult {
  return {
    importedCounts,
    skippedCounts,
    warnings,
  };
}
